import { useEffect, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import LangToggle from "@/components/LangToggle";
import { projectPages, projectPageOrder } from "@/data/projectsData";
import { useLang } from "@/i18n/LanguageContext";

export default function ProjectPage({ lenisRef }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [lightbox, setLightbox] = useState(null);
  const project = projectPages[slug];
  const totalPages = project?.pages?.length ?? (project ? project.total - (project.skip || []).length : 0);

  useEffect(() => {
    setLightbox(null);
    lenisRef?.current?.scrollTo(0, { immediate: true });
  }, [slug, lenisRef]);

  useEffect(() => {
    if (lightbox !== null) {
      lenisRef?.current?.stop();
    } else {
      lenisRef?.current?.start();
    }
  }, [lightbox, lenisRef]);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null || !project) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((v) => Math.min(v + 1, totalPages - 1));
      if (e.key === "ArrowLeft") setLightbox((v) => Math.max(v - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, project]);

  if (!project) return <Navigate to="/" replace />;

  const pages =
    project.pages ||
    Array.from(
      { length: project.total },
      (_, i) => `${project.dir}/page-${String(i + 1).padStart(2, "0")}.webp`
    ).filter((_, i) => !(project.skip || []).includes(i + 1));
  const total = pages.length;
  const nextSlug = projectPageOrder[(projectPageOrder.indexOf(slug) + 1) % projectPageOrder.length];
  const nextProject = projectPages[nextSlug];
  const nextTitle = nextProject.titleBottom
    ? `${nextProject.titleTop} ${nextProject.titleBottom}`
    : nextProject.titleTop;
  const title = project.titleBottom ? `${project.titleTop} ${project.titleBottom}` : project.titleTop;

  const tagline = lang === "en" && project.taglineEn ? project.taglineEn : project.tagline;
  const description = lang === "en" && project.descriptionEn ? project.descriptionEn : project.description;
  const meta = lang === "en" && project.metaEn ? project.metaEn : project.meta;
  const videoLabel = lang === "en" && project.videoLabelEn ? project.videoLabelEn : project.videoLabel;

  return (
    <div data-testid="project-page" className="min-h-screen bg-[#050505]">
      <header className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-between px-6 md:px-12 py-5">
        <button
          data-testid="back-to-projects-button"
          onClick={() => navigate("/")}
          className="group inline-flex items-center gap-3 font-mono-tech text-xs tracking-[0.3em] text-[#E5E5E5] hover:text-[#75FB4E] transition-colors duration-300 drop-shadow-[0_1px_6px_rgba(5,5,5,0.9)]"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
          {t("pp.allProjects")}
        </button>
        <div className="flex items-center gap-5">
          <LangToggle />
          <span className="font-mono-tech text-xs tracking-[0.3em] text-[#E5E5E5]">CR—L ©26</span>
        </div>
      </header>

      <section className="px-6 md:px-12 pt-32 md:pt-44 pb-16 md:pb-24">
        <FadeUp className="flex items-baseline gap-4">
          <span className="font-mono-tech text-sm text-[#75FB4E]">({project.num})</span>
          <span className="font-mono-tech text-xs tracking-widest text-[#808080] uppercase">
            {tagline} — {project.year}
          </span>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="font-display uppercase leading-[0.85] text-[13vw] md:text-[10vw] mt-6">
            <span className="block text-[#E5E5E5]">{project.titleTop}</span>
            {project.titleBottom && <span className="block text-outline">{project.titleBottom}</span>}
          </h1>
        </FadeUp>
        <FadeUp delay={0.2} className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-[#1A1A1A] pt-8">
          {meta.map((m) => (
            <div key={m.label}>
              <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#808080] uppercase mb-2">{m.label}</p>
              <p className="text-sm text-[#E5E5E5]">{m.value}</p>
            </div>
          ))}
        </FadeUp>
        <FadeUp delay={0.25} className="mt-12 max-w-xl">
          <p className="text-base md:text-lg font-light leading-relaxed text-[#808080]">{description}</p>
        </FadeUp>
      </section>

      {project.video && (
        <section className="px-6 md:px-12 pb-16 md:pb-24">
          <FadeUp className="flex items-baseline justify-between mb-6">
            <h2 className="font-mono-tech text-xs tracking-[0.3em] text-[#808080] uppercase">{t("pp.film")} — {videoLabel}</h2>
            <span className="font-mono-tech text-xs text-[#75FB4E]">{t("pp.motion")}</span>
          </FadeUp>
          <FadeUp>
            <video
              data-testid="project-video"
              className="w-full h-auto border border-[#1A1A1A]"
              autoPlay
              muted
              loop
              playsInline
              controls
            >
              <source src={project.video} type="video/mp4" />
              {project.videoWebm && <source src={project.videoWebm} type="video/webm" />}
            </video>
          </FadeUp>
        </section>
      )}

      <section className="px-6 md:px-12 pb-24">
        <FadeUp className="flex items-baseline justify-between mb-10">
          <h2 className="font-mono-tech text-xs tracking-[0.3em] text-[#808080] uppercase">{t("pp.gallery")}</h2>
          <span className="font-mono-tech text-xs text-[#75FB4E]">{total} {project.pages ? t("pp.visuals") : t("pp.pages")}</span>
        </FadeUp>
        <div className="grid grid-cols-12 gap-4 md:gap-5 items-start">
          {pages.map((src, i) => {
            const full = project.featured ? project.featured.includes(i + 1) : false;
            const span = total <= 2 ? "md:col-span-6" : full ? "md:col-span-6" : "md:col-span-4";
            return (
              <FadeUp key={src} className={`col-span-6 ${span}`}>
                <button
                  data-testid={`project-page-${i + 1}`}
                  onClick={() => setLightbox(i)}
                  className="group relative block w-full overflow-hidden border border-[#1A1A1A] hover:border-[#75FB4E]/50 transition-colors duration-500"
                >
                  <img
                    src={src}
                    alt={`${title} — ${i + 1}`}
                    loading="lazy"
                    className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700 ease-out"
                  />
                  <span className="absolute top-3 left-3 font-mono-tech text-[10px] tracking-widest bg-[#050505]/80 px-2 py-1 text-[#808080] group-hover:text-[#75FB4E] transition-colors duration-300">
                    {String(i + 1).padStart(2, "0")} / {total}
                  </span>
                </button>
              </FadeUp>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-[#1A1A1A] px-6 md:px-12 py-16 text-center">
        <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#808080] uppercase mb-6">{t("pp.nextProject")}</p>
        <button
          data-testid="next-project-button"
          onClick={() => navigate(`/projet/${nextSlug}`)}
          className="font-display text-4xl md:text-6xl uppercase text-outline"
        >
          {nextTitle}
        </button>
      </footer>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            data-testid="lightbox"
            className="fixed inset-0 z-[110] bg-[#050505] flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between px-6 md:px-12 py-5">
              <span data-testid="lightbox-counter" className="font-mono-tech text-xs tracking-[0.3em] text-[#808080]">
                {String(lightbox + 1).padStart(2, "0")} / {total}
              </span>
              <button
                data-testid="lightbox-close"
                onClick={() => setLightbox(null)}
                className="group inline-flex items-center gap-2 font-mono-tech text-xs tracking-[0.3em] text-[#E5E5E5] hover:text-[#75FB4E] transition-colors duration-300"
              >
                {t("pp.close")} <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center px-6 md:px-24 pb-6 min-h-0">
              <motion.img
                key={lightbox}
                src={pages[lightbox]}
                alt={`${title} — ${lightbox + 1}`}
                data-testid="lightbox-image"
                className="max-w-full max-h-full object-contain"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <div className="flex items-center justify-center gap-6 pb-8">
              <button
                data-testid="lightbox-prev"
                onClick={() => setLightbox((v) => Math.max(v - 1, 0))}
                disabled={lightbox === 0}
                className="group inline-flex items-center gap-2 border border-[#2A2A2A] px-6 py-3 font-mono-tech text-[10px] tracking-[0.3em] text-[#E5E5E5] hover:border-[#75FB4E] hover:text-[#75FB4E] transition-colors duration-300 disabled:opacity-30 disabled:pointer-events-none"
              >
                <ArrowLeft className="w-4 h-4" /> {t("pp.prev")}
              </button>
              <button
                data-testid="lightbox-next"
                onClick={() => setLightbox((v) => Math.min(v + 1, total - 1))}
                disabled={lightbox === total - 1}
                className="group inline-flex items-center gap-2 border border-[#2A2A2A] px-6 py-3 font-mono-tech text-[10px] tracking-[0.3em] text-[#E5E5E5] hover:border-[#75FB4E] hover:text-[#75FB4E] transition-colors duration-300 disabled:opacity-30 disabled:pointer-events-none"
              >
                {t("pp.next")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
